import { createSlice } from '@reduxjs/toolkit';
import { callApi } from '../../utils/apiCall';
import { dispatch } from '../store';

// Read cached PMS enabled state for instant UI on load
const cachedPmsEnabled = (() => {
    try {
        return localStorage.getItem('pmsEnabledGlobal') === 'true';
    } catch {
        return false;
    }
})();

const initialState = {
    isLoading: false,
    pmsEnabledGlobal: cachedPmsEnabled,
    schedules: [],
    slots: [],
    overrides: [],
    error: null,
};

const slice = createSlice({
    name: 'pmsSchedule',
    initialState,
    reducers: {
        reset(state) {
            Object.assign(state, { ...initialState, pmsEnabledGlobal: false });
            try { localStorage.removeItem('pmsEnabledGlobal'); } catch { /* noop */ }
        },
        updateLoader(state, action) {
            state.isLoading = action.payload;
        },
        hasError(state, action) {
            state.isLoading = false;
            state.error = action.payload;
        },
        updatePmsEnabledGlobal(state, action) {
            state.pmsEnabledGlobal = action.payload;
            try { localStorage.setItem('pmsEnabledGlobal', String(action.payload)); } catch { /* noop */ }
        },
        setSchedules(state, action) {
            state.isLoading = false;
            state.schedules = action.payload;
        },
        setSlots(state, action) {
            state.isLoading = false;
            state.slots = action.payload;
        },
        setOverrides(state, action) {
            state.isLoading = false;
            state.overrides = action.payload;
        },
    },
});

export default slice.reducer;

export const { updatePmsEnabledGlobal } = slice.actions;

// Check whether PMS scheduling is enabled for a practice via preferences endpoint
export function checkPmsEnabled(practiceId) {
    return async () => {
        try {
            const response = await callApi(`/api/v1/practices/${practiceId}/preferences/`);
            const isEnabled = response?.data?.is_pms_scheduling_enabled === true;
            dispatch(slice.actions.updatePmsEnabledGlobal(isEnabled));
        } catch {
            dispatch(slice.actions.updatePmsEnabledGlobal(false));
        }
    };
}

export function fetchPractitionerSchedules(practiceId, practitionerId) {
    return async () => {
        try {
            dispatch(slice.actions.updateLoader(true));
            const response = await callApi(
                `/api/v1/practices/${practiceId}/pms/practitioners/${practitionerId}/schedules/`
            );
            const results = response?.data?.results ?? response?.data ?? [];
            dispatch(slice.actions.setSchedules(Array.isArray(results) ? results : []));
        } catch (error) {
            dispatch(slice.actions.hasError(error));
        }
    };
}

export function createSchedule(practiceId, practitionerId, data, onSuccess) {
    return async () => {
        try {
            const response = await callApi(
                `/api/v1/practices/${practiceId}/pms/practitioners/${practitionerId}/schedules/`,
                'POST',
                data
            );
            if (response.ok && onSuccess) {
                onSuccess(response.data);
            }
            return response;
        } catch (error) {
            dispatch(slice.actions.hasError(error));
            return { ok: false };
        }
    };
}

export function updateSchedule(practiceId, practitionerId, scheduleId, data, onSuccess) {
    return async () => {
        try {
            const response = await callApi(
                `/api/v1/practices/${practiceId}/pms/practitioners/${practitionerId}/schedules/${scheduleId}/`,
                'PATCH',
                data
            );
            if (response.ok && onSuccess) {
                onSuccess(response.data);
            }
            return response;
        } catch (error) {
            dispatch(slice.actions.hasError(error));
            return { ok: false };
        }
    };
}

export function deleteSchedule(practiceId, practitionerId, scheduleId, onSuccess) {
    return async () => {
        try {
            const response = await callApi(
                `/api/v1/practices/${practiceId}/pms/practitioners/${practitionerId}/schedules/${scheduleId}/`,
                'DELETE'
            );
            if (response.ok && onSuccess) {
                onSuccess();
            }
            return response;
        } catch (error) {
            dispatch(slice.actions.hasError(error));
            return { ok: false };
        }
    };
}

export function fetchSlots(practiceId, practitionerId, scheduleId, params) {
    return async () => {
        try {
            dispatch(slice.actions.updateLoader(true));
            const queryString = params
                ? `?${new URLSearchParams(params).toString()}`
                : '';
            const response = await callApi(
                `/api/v1/practices/${practiceId}/pms/practitioners/${practitionerId}/schedules/${scheduleId}/slots/${queryString}`
            );
            const results = response?.data?.results ?? response?.data ?? [];
            dispatch(slice.actions.setSlots(Array.isArray(results) ? results : []));
        } catch (error) {
            dispatch(slice.actions.hasError(error));
        }
    };
}

export function createOverride(practiceId, practitionerId, scheduleId, data, onSuccess) {
    return async () => {
        try {
            const response = await callApi(
                `/api/v1/practices/${practiceId}/pms/practitioners/${practitionerId}/schedules/${scheduleId}/overrides/`,
                'POST',
                data
            );
            if (response.ok && onSuccess) {
                onSuccess(response.data);
            }
            return response;
        } catch (error) {
            dispatch(slice.actions.hasError(error));
            return { ok: false };
        }
    };
}

export function deleteOverride(practiceId, practitionerId, scheduleId, overrideId, onSuccess) {
    return async () => {
        try {
            const response = await callApi(
                `/api/v1/practices/${practiceId}/pms/practitioners/${practitionerId}/schedules/${scheduleId}/overrides/${overrideId}/`,
                'DELETE'
            );
            if (response.ok && onSuccess) {
                onSuccess();
            }
            return response;
        } catch (error) {
            dispatch(slice.actions.hasError(error));
            return { ok: false };
        }
    };
}
